import { useMemo } from "react";
import type { Components } from "react-markdown";
import { Markdown } from "./markdown";
import { markLedgerStages, parseStageMark, stagesNotInForce, type LedgerStage, type StageGroup } from "./quality";

/**
 * The sources ledger (the facts behind an article, each with its address and a verbatim quote). A fact about a law carries a
 * `stage:` line (proposed / first reading / royal assent / in force ...); it is drawn as a badge so a rule that is not law yet
 * cannot be mistaken for one.
 */

const STAGE_STYLE: Record<StageGroup, { cls: string; help: string }> = {
  pending: { cls: "border-amber-500/40 bg-amber-500/15 text-amber-200", help: "Not law yet: proposed, or still moving through the legislature." },
  passed: { cls: "border-sky-500/40 bg-sky-500/15 text-sky-200", help: "Passed or given Royal Assent, but it may not be in effect yet." },
  in_force: { cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-200", help: "In force: it applies now." },
  ended: { cls: "border-red-500/40 bg-red-500/15 text-red-200", help: "Withdrawn, repealed or lapsed." },
  other: { cls: "border-border bg-muted/40 text-foreground/80", help: "The legislative stage the writer recorded." },
};

export function StageBadge({ stage }: { stage: LedgerStage }) {
  const s = STAGE_STYLE[stage.group];
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 align-middle text-[11px] font-medium not-prose ${s.cls}`}
      title={s.help}
      data-testid={`stage-badge-${stage.group}`}
    >
      {stage.label}
    </span>
  );
}

const COMPONENTS: Components = {
  code({ node: _n, children, className, ...props }) {
    const only = Array.isArray(children) ? (children.length === 1 ? children[0] : null) : children;
    const stage = typeof only === "string" ? parseStageMark(only) : null;
    if (stage) return <StageBadge stage={stage} />;
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
};

/** The stages named in a ledger and how many of them are not law in force yet, for the panel header and the Checks panel. */
export function ledgerStageSummary(text: string): { stages: LedgerStage[]; notInForce: number } {
  const { stages } = markLedgerStages(text);
  return { stages, notInForce: stagesNotInForce(stages) };
}

export function LedgerBody({ text }: { text: string }) {
  const marked = useMemo(() => markLedgerStages(text).text, [text]);
  return <Markdown components={COMPONENTS}>{marked}</Markdown>;
}
